import { useEffect, useState } from 'react';
import { clienteService, pagoService, prestamoService } from '../../services/api';
import '../../styles/Reportes.css';

const VerificarCompromiso = () => {
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    // Establecer fechas por defecto (última semana)
    const hoy = new Date();
    const haceUnaSemana = new Date();
    haceUnaSemana.setDate(haceUnaSemana.getDate() - 7);

    setFechaFin(hoy.toISOString().split('T')[0]);
    setFechaInicio(haceUnaSemana.toISOString().split('T')[0]);
  }, []);

  const cargarDatos = async () => {
    if (!fechaInicio || !fechaFin) {
      alert('Por favor selecciona ambas fechas');
      return;
    }

    setCargando(true);
    try {
      const [responsePrestamos, responsePagos, responseClientes] = await Promise.all([
        prestamoService.getAll(),
        pagoService.getAll(),
        clienteService.getAll()
      ]);

      const todosPrestamos = responsePrestamos.data.prestamos || [];
      const todosPagos = responsePagos.data.pagos || [];
      const todosClientes = responseClientes.data.clientes || [];

      const inicio = new Date(fechaInicio);
      const fin = new Date(fechaFin);
      fin.setHours(23, 59, 59);

      // Mapa de clientes
      const mapaClientes = todosClientes.reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
      }, {});

      // Filtrar préstamos activos
      const prestamosActivos = todosPrestamos.filter(p =>
        p.estado === 'activo' || p.estado === 'atrasado'
      );

      // Analizar cada préstamo
      const analisis = prestamosActivos.map(prestamo => {
        const cliente = mapaClientes[prestamo.cliente_id] || {};

        // Calcular cuotas esperadas en el período
        const fechaInicioPrestamo = new Date(prestamo.fecha_inicio || prestamo.fecha_efectividad);
        let cuotasEsperadas = 0;

        if (prestamo.modalidad === 'Diario') {
          const diasEnPeriodo = Math.floor((fin - Math.max(inicio, fechaInicioPrestamo)) / (1000 * 60 * 60 * 24));
          cuotasEsperadas = Math.max(0, diasEnPeriodo);
        } else if (prestamo.modalidad === 'Semanal') {
          const semanasEnPeriodo = Math.floor((fin - Math.max(inicio, fechaInicioPrestamo)) / (1000 * 60 * 60 * 24 * 7));
          cuotasEsperadas = Math.max(0, semanasEnPeriodo);
        } else if (prestamo.modalidad === 'Quincenal') {
          const quincenasEnPeriodo = Math.floor((fin - Math.max(inicio, fechaInicioPrestamo)) / (1000 * 60 * 60 * 24 * 15));
          cuotasEsperadas = Math.max(0, quincenasEnPeriodo);
        } else {
          const mesesEnPeriodo = Math.floor((fin - Math.max(inicio, fechaInicioPrestamo)) / (1000 * 60 * 60 * 24 * 30));
          cuotasEsperadas = Math.max(0, mesesEnPeriodo);
        }

        // Contar pagos realizados en el período
        const pagosEnPeriodo = todosPagos.filter(pago => {
          const fechaPago = new Date(pago.fecha_pago);
          return pago.prestamo_id === prestamo.id &&
                 fechaPago >= inicio &&
                 fechaPago <= fin;
        });

        const cuotasPagadas = pagosEnPeriodo.length;
        const cumplimiento = cuotasEsperadas > 0 ? (cuotasPagadas / cuotasEsperadas) * 100 : 0;
        const deficit = Math.max(0, cuotasEsperadas - cuotasPagadas);

        return {
          prestamo_id: prestamo.id,
          cliente_nombre: `${cliente.nombres || ''} ${cliente.apellidos || ''}`.trim() || 'Desconocido',
          cliente_telefono: cliente.celular || cliente.telefono || 'N/A',
          modalidad: prestamo.modalidad,
          cuotas_esperadas: cuotasEsperadas,
          cuotas_pagadas: cuotasPagadas,
          deficit,
          cumplimiento: cumplimiento.toFixed(1),
          estado: prestamo.estado,
          monto_total: prestamo.total,
          cuota_valor: prestamo.cuotas > 0 ? (prestamo.total / prestamo.cuotas) : 0
        };
      }).filter(a => a.cuotas_esperadas > 0);

      // Ordenar por déficit descendente
      analisis.sort((a, b) => b.deficit - a.deficit);

      // Calcular estadísticas
      const totalEsperado = analisis.reduce((sum, a) => sum + a.cuotas_esperadas, 0);
      const totalPagado = analisis.reduce((sum, a) => sum + a.cuotas_pagadas, 0);
      const totalDeficit = analisis.reduce((sum, a) => sum + a.deficit, 0);
      const cumplimientoGeneral = totalEsperado > 0 ? (totalPagado / totalEsperado) * 100 : 0;

      const conCumplimiento = analisis.filter(a => a.cumplimiento >= 100).length;
      const conRetraso = analisis.filter(a => a.cumplimiento < 100 && a.cumplimiento >= 50).length;
      const criticos = analisis.filter(a => a.cumplimiento < 50).length;

      setDatos({
        analisis,
        totalEsperado,
        totalPagado,
        totalDeficit,
        cumplimientoGeneral: cumplimientoGeneral.toFixed(1),
        conCumplimiento,
        conRetraso,
        criticos
      });
    } catch (error) {
      alert('Error al analizar compromisos');
      console.error(error);
    } finally {
      setCargando(false);
    }
  };

  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleDateString('es-DO');
  };

  const getBadgeCumplimiento = (cumplimiento) => {
    const valor = parseFloat(cumplimiento);
    if (valor >= 100) return 'badge-pagado';
    if (valor >= 75) return 'badge-activo';
    if (valor >= 50) return 'badge-pendiente';
    return 'badge-vencido';
  };

  return (
    <div className="reporte-detalle">
      <div className="reporte-header">
        <h1>✅ Verificar Compromiso</h1>
        <p>Comparación entre pagos esperados y pagos realizados</p>
      </div>

      <div className="filtros-container">
        <div className="filtro-grupo">
          <label>Fecha Inicial:</label>
          <input
            type="date"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
            className="input-text"
          />
        </div>

        <div className="filtro-grupo">
          <label>Fecha Final:</label>
          <input
            type="date"
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            className="input-text"
          />
        </div>

        <button onClick={cargarDatos} className="btn-buscar">
          Analizar
        </button>
      </div>

      {cargando && (
        <div className="loading">Analizando compromisos de pago...</div>
      )}

      {!cargando && datos && (
        <>
          <div className="info-prestamo">
            <h3>Período: {formatearFecha(fechaInicio)} - {formatearFecha(fechaFin)}</h3>
          </div>

          <div className="resumen-cards">
            <div className="card-resumen" style={{ borderLeft: '4px solid #3498db' }}>
              <div className="card-icono">📋</div>
              <div className="card-info">
                <div className="card-valor">{datos.totalEsperado}</div>
                <div className="card-label">Cuotas Esperadas</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #27ae60' }}>
              <div className="card-icono">✅</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#27ae60' }}>{datos.totalPagado}</div>
                <div className="card-label">Cuotas Pagadas</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: '4px solid #e74c3c' }}>
              <div className="card-icono">❌</div>
              <div className="card-info">
                <div className="card-valor" style={{ color: '#e74c3c' }}>{datos.totalDeficit}</div>
                <div className="card-label">Déficit de Cuotas</div>
              </div>
            </div>

            <div className="card-resumen" style={{ borderLeft: `4px solid ${datos.cumplimientoGeneral >= 80 ? '#27ae60' : datos.cumplimientoGeneral >= 60 ? '#f39c12' : '#e74c3c'}` }}>
              <div className="card-icono">📊</div>
              <div className="card-info">
                <div className="card-valor" style={{
                  color: datos.cumplimientoGeneral >= 80 ? '#27ae60' : datos.cumplimientoGeneral >= 60 ? '#f39c12' : '#e74c3c'
                }}>
                  {datos.cumplimientoGeneral}%
                </div>
                <div className="card-label">Cumplimiento General</div>
              </div>
            </div>
          </div>

          <div className="info-prestamo" style={{ marginTop: '20px' }}>
            <h3>� Distribución de Cumplimiento</h3>
            <div className="info-grid">
              <div>
                <strong>✅ Al día (≥100%):</strong> {datos.conCumplimiento} préstamos
              </div>
              <div>
                <strong>⚠️ Con retraso (50-99%):</strong> {datos.conRetraso} préstamos
              </div>
              <div>
                <strong>🚨 Críticos (&lt;50%):</strong> {datos.criticos} préstamos
              </div>
            </div>
          </div>

          <div className="tabla-container">
            <table className="tabla-reporte">
              <thead>
                <tr>
                  <th>Préstamo ID</th>
                  <th>Cliente</th>
                  <th>Teléfono</th>
                  <th>Modalidad</th>
                  <th>Esperadas</th>
                  <th>Pagadas</th>
                  <th>Déficit</th>
                  <th>Cumplimiento</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {datos.analisis.map((item, index) => (
                  <tr key={index}>
                    <td>{item.prestamo_id}</td>
                    <td><strong>{item.cliente_nombre}</strong></td>
                    <td>{item.cliente_telefono}</td>
                    <td>{item.modalidad}</td>
                    <td>{item.cuotas_esperadas}</td>
                    <td className={item.cuotas_pagadas >= item.cuotas_esperadas ? 'destacado' : ''}>
                      {item.cuotas_pagadas}
                    </td>
                    <td style={{ color: item.deficit > 0 ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>
                      {item.deficit}
                    </td>
                    <td>
                      <span className={`badge ${getBadgeCumplimiento(item.cumplimiento)}`}>
                        {item.cumplimiento}%
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${item.estado}`}>
                        {item.estado}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!cargando && !datos && (
        <div className="sin-datos">
          <p>Selecciona un período y genera el análisis</p>
        </div>
      )}
    </div>
  );
};

export default VerificarCompromiso;
